import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveKit } from "@/contexts/LiveKitContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EventChatWidget from "@/components/EventChatWidget";
import {
  Mic, MicOff, Video, VideoOff, Monitor, MonitorOff, Hand, Users,
  MessageCircle, PhoneOff, Maximize2, Minimize2,
  MoreVertical, ChevronUp, Shield, Crown, Star,
  User as UserIcon, ArrowLeft, Clock, X, AlertTriangle, HelpCircle,
} from "lucide-react";

// ===== SIMULATED PARTICIPANTS =====
const DEMO_PARTICIPANTS = [
  { id: "sim-1", name: "Marie Dupont", role: "speaker", avatar: "MD", color: "#0099CC", micOn: true, camOn: true },
  { id: "sim-2", name: "Jean Mukendi", role: "speaker", avatar: "JM", color: "#10B981", micOn: true, camOn: true },
  { id: "sim-3", name: "Aisha Diallo", role: "speaker", avatar: "AD", color: "#8B5CF6", micOn: false, camOn: true },
  { id: "sim-4", name: "Pierre Ngoy", role: "moderator", avatar: "PN", color: "#F59E0B", micOn: true, camOn: false },
  { id: "sim-5", name: "Sophie Laurent", role: "participant", avatar: "SL", color: "#EF4444", micOn: false, camOn: true },
  { id: "sim-6", name: "David Kalala", role: "participant", avatar: "DK", color: "#06B6D4", micOn: false, camOn: false },
];

const ROLE_LABELS = { speaker: "Intervenant", moderator: "Moderateur", participant: "Participant", organizer: "Organisateur" };
const ROLE_ICONS = { speaker: Star, moderator: Shield, participant: UserIcon, organizer: Crown };

// ===== SIMULATED VIDEO TILE =====
function SimulatedVideoTile({ participant, isLocal, onClick }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!participant.camOn || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    let frame = 0;
    const animate = () => {
      if (!canvasRef.current) return;
      const w = canvasRef.current.width;
      const h = canvasRef.current.height;
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, "#0B1120");
      grad.addColorStop(1, "#151B2B");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      const cx = w / 2 + Math.sin(frame * 0.02) * 8;
      const cy = h / 2 - 10 + Math.cos(frame * 0.015) * 5;
      const r = Math.min(w, h) * 0.18;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = participant.color + "40";
      ctx.fill();
      ctx.strokeStyle = participant.color;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = participant.color;
      ctx.font = `bold ${r * 0.7}px system-ui`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(participant.avatar, cx, cy);
      if (participant.micOn) {
        const pulse = 0.5 + Math.sin(frame * 0.1) * 0.5;
        ctx.beginPath();
        ctx.arc(cx + r + 8, cy - r + 8, 4 + pulse * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(16, 185, 129, ${0.4 + pulse * 0.4})`;
        ctx.fill();
      }
      frame++;
      return requestAnimationFrame(animate);
    };
    const id = animate();
    return () => cancelAnimationFrame(id);
  }, [participant.camOn, participant.micOn, participant.color, participant.avatar]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-[#2A3441] hover:border-[#4A5568] transition-all cursor-pointer group" onClick={onClick}
      data-testid={`video-tile-${participant.id}`}>
      {participant.camOn ? (
        <canvas ref={canvasRef} width={640} height={360} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-[#0B1120] flex items-center justify-center min-h-[120px]">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold" style={{ background: participant.color + "20", color: participant.color }}>
            {participant.avatar}
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        <div className="flex items-center gap-1.5">
          {participant.micOn ? <Mic className="h-3 w-3 text-[#10B981]" /> : <MicOff className="h-3 w-3 text-red-400" />}
          <span className="text-white text-xs font-medium truncate">{participant.name}</span>
          {isLocal && <span className="text-[10px] text-[#0099CC] font-semibold">(Vous)</span>}
        </div>
        <span className="text-[10px] text-[#64748b]">{ROLE_LABELS[participant.role]}</span>
      </div>
      {participant.micOn && (
        <div className="absolute top-2 right-2">
          <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
        </div>
      )}
    </div>
  );
}

// ===== LIVE VIDEO TILE (LiveKit real) =====
function LiveVideoTile({ track, participant: lkParticipant, isLocal }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (track && videoRef.current) {
      track.attach(videoRef.current);
      return () => { if (videoRef.current) track.detach(videoRef.current); };
    }
  }, [track]);

  const name = lkParticipant?.name || lkParticipant?.identity || "Inconnu";
  const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="relative rounded-xl overflow-hidden border border-[#2A3441] hover:border-[#4A5568] transition-all" data-testid={`live-tile-${lkParticipant?.identity}`}>
      {track ? (
        <video ref={videoRef} autoPlay playsInline muted={isLocal} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-[#0B1120] flex items-center justify-center min-h-[120px]">
          <div className="w-16 h-16 rounded-full bg-[#0099CC]/20 flex items-center justify-center text-xl font-bold text-[#0099CC]">
            {initials}
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        <div className="flex items-center gap-1.5">
          {lkParticipant?.isMicrophoneEnabled ? <Mic className="h-3 w-3 text-[#10B981]" /> : <MicOff className="h-3 w-3 text-red-400" />}
          <span className="text-white text-xs font-medium truncate">{name}</span>
          {isLocal && <span className="text-[10px] text-[#0099CC] font-semibold">(Vous)</span>}
        </div>
      </div>
    </div>
  );
}

// ===== MAIN COMPONENT =====
export default function LiveMeetingPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // Global LiveKit context (persistent across navigation)
  const lk = useLiveKit();

  // UI-only state
  const [handRaised, setHandRaised] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [spotlight, setSpotlight] = useState(null);
  const [sidePanel, setSidePanel] = useState("chat");
  const [mobileSideOpen, setMobileSideOpen] = useState(false);
  const [simParticipants, setSimParticipants] = useState(DEMO_PARTICIPANTS);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);

  // Join room on mount (context handles idempotency)
  useEffect(() => {
    if (roomId && lk.connectionState === "disconnected") {
      lk.joinRoom(roomId);
    }
  }, [roomId]);

  const isLive = lk.livekitMode === "live";
  const isSimulation = !isLive;

  // Local participant (for simulation mode)
  const localParticipant = {
    id: "local",
    name: user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : "Vous",
    role: "organizer",
    avatar: user?.first_name ? `${user.first_name[0]}${(user.last_name || "")[0] || ""}` : "V",
    color: "#0099CC",
    micOn: lk.micOn,
    camOn: lk.camOn,
  };

  const allSimParticipants = [localParticipant, ...simParticipants];
  const gridParticipants = spotlight ? allSimParticipants.filter(p => p.id !== spotlight) : allSimParticipants;
  const spotlightParticipant = spotlight ? allSimParticipants.find(p => p.id === spotlight) : null;

  const toggleParticipantMic = (id) => {
    setSimParticipants(ps => ps.map(p => p.id === id ? { ...p, micOn: !p.micOn } : p));
    toast.success("Micro mis a jour");
  };

  // Navigate back WITHOUT disconnecting (minimize)
  const minimizeMeeting = () => {
    navigate(-1);
  };

  const participantCount = isLive ? lk.lkParticipants.length : allSimParticipants.length;

  // Render video grid
  const renderVideoGrid = () => {
    if (isLive && lk.lkParticipants.length > 0) {
      return (
        <div className={`h-full grid gap-2 ${
          lk.lkParticipants.length <= 1 ? "grid-cols-1" :
          lk.lkParticipants.length <= 4 ? "grid-cols-2" :
          lk.lkParticipants.length <= 6 ? "grid-cols-3" : "grid-cols-4"
        }`}>
          {lk.lkParticipants.map(p => {
            const videoTrack = Array.from(p.videoTrackPublications?.values() || [])
              .find(pub => pub.track && pub.source === 'camera')?.track;
            const isLocal = p === lk.lkRoom?.localParticipant;
            return <LiveVideoTile key={p.identity} track={videoTrack} participant={p} isLocal={isLocal} />;
          })}
        </div>
      );
    }

    return (
      <>
        {spotlightParticipant && (
          <div className="h-[60%] mb-2">
            <SimulatedVideoTile participant={spotlightParticipant} isLocal={spotlightParticipant.id === "local"} onClick={() => setSpotlight(null)} />
          </div>
        )}
        <div className={`${spotlightParticipant ? "h-[38%]" : "h-full"} grid gap-2 ${
          gridParticipants.length <= 1 ? "grid-cols-1" :
          gridParticipants.length <= 4 ? "grid-cols-2" :
          gridParticipants.length <= 6 ? "grid-cols-3" : "grid-cols-4"
        }`}>
          {gridParticipants.map(p => (
            <SimulatedVideoTile key={p.id} participant={p} isLocal={p.id === "local"} onClick={() => setSpotlight(p.id === spotlight ? null : p.id)} />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={`h-screen bg-[#0B0F19] flex flex-col overflow-hidden ${fullscreen ? "fixed inset-0 z-50" : ""}`} data-testid="live-meeting-page">
      {/* Header */}
      <header className="h-12 bg-[#0B0F19] border-b border-[#1E293B] flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={minimizeMeeting} className="text-[#64748b] hover:text-white p-1" title="Reduire — la conference reste active" data-testid="minimize-btn">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isLive ? "bg-[#10B981]" : "bg-red-500"} animate-pulse`} />
            <span className="text-white text-sm font-semibold hidden sm:inline">GLOTECH Live Meeting</span>
            <span className="text-[#64748b] text-xs hidden sm:inline">|</span>
            <span className="text-[#94A3B8] text-xs truncate max-w-[200px]">{roomId || "Salle"}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[#94A3B8] text-xs" data-testid="meeting-timer">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-mono">{lk.formatTime(lk.elapsed)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[#94A3B8] text-xs">
            <Users className="h-3.5 w-3.5" />
            <span>{participantCount}</span>
          </div>
          {/* Media indicators */}
          <div className="flex items-center gap-1">
            {lk.micOn && <Mic className="h-3 w-3 text-[#10B981]" />}
            {!lk.micOn && <MicOff className="h-3 w-3 text-red-400" />}
            {lk.camOn && <Video className="h-3 w-3 text-[#3B82F6]" />}
            {!lk.camOn && <VideoOff className="h-3 w-3 text-red-400" />}
            {lk.screenShare && <Monitor className="h-3 w-3 text-[#F59E0B]" />}
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
            isLive ? "bg-[#10B981]/20 text-[#10B981]" :
            lk.connectionState === "connecting" ? "bg-[#64748b]/20 text-[#64748b]" :
            "bg-[#F59E0B]/20 text-[#F59E0B]"
          }`} data-testid="mode-badge">
            {isLive ? "EN DIRECT" : lk.connectionState === "connecting" ? "CONNEXION..." : "SIMULATION"}
          </span>
          <button onClick={() => setFullscreen(!fullscreen)} className="text-[#64748b] hover:text-white p-1">
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Video grid */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 p-2 sm:p-3 overflow-hidden" data-testid="video-grid">
            {renderVideoGrid()}
          </div>

          {lk.screenShare && (
            <div className="mx-3 mb-2 bg-[#0B1120] border border-[#1E3A5F] rounded-xl p-4 text-center" data-testid="screen-share-preview">
              <Monitor className="h-8 w-8 text-[#3B82F6] mx-auto mb-1" />
              <p className="text-[#3B82F6] text-sm font-semibold">Partage d'ecran actif</p>
            </div>
          )}

          {/* Controls bar */}
          <div className="h-16 bg-[#0B0F19] border-t border-[#1E293B] flex items-center justify-center gap-2 sm:gap-3 px-3 shrink-0" data-testid="controls-bar">
            <button data-testid="toggle-mic" onClick={lk.toggleMic}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${lk.micOn ? "bg-[#1E293B] hover:bg-[#2A3441] text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}>
              {lk.micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            </button>
            <button data-testid="toggle-cam" onClick={lk.toggleCam}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${lk.camOn ? "bg-[#1E293B] hover:bg-[#2A3441] text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}>
              {lk.camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
            </button>
            <button data-testid="toggle-screen" onClick={lk.toggleScreenShare}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${lk.screenShare ? "bg-[#3B82F6] text-white" : "bg-[#1E293B] hover:bg-[#2A3441] text-white"}`}>
              {lk.screenShare ? <MonitorOff className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
            </button>
            <button data-testid="toggle-hand" onClick={() => { setHandRaised(!handRaised); toast.success(handRaised ? "Main baissee" : "Main levee"); }}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${handRaised ? "bg-[#F59E0B] text-black" : "bg-[#1E293B] hover:bg-[#2A3441] text-white"}`}>
              <Hand className="h-5 w-5" />
            </button>
            <div className="w-px h-8 bg-[#2A3441] mx-1 hidden sm:block" />
            <button data-testid="toggle-chat" onClick={() => { setSidePanel(sidePanel === "chat" ? null : "chat"); setMobileSideOpen(true); }}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${sidePanel === "chat" ? "bg-[#0099CC] text-white" : "bg-[#1E293B] hover:bg-[#2A3441] text-white"}`}>
              <MessageCircle className="h-5 w-5" />
            </button>
            <button data-testid="toggle-qa" onClick={() => { setSidePanel(sidePanel === "qa" ? null : "qa"); setMobileSideOpen(true); }}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${sidePanel === "qa" ? "bg-[#8B5CF6] text-white" : "bg-[#1E293B] hover:bg-[#2A3441] text-white"}`}>
              <HelpCircle className="h-5 w-5" />
            </button>
            <button data-testid="toggle-participants" onClick={() => { setSidePanel(sidePanel === "participants" ? null : "participants"); setMobileSideOpen(true); }}
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${sidePanel === "participants" ? "bg-[#10B981] text-white" : "bg-[#1E293B] hover:bg-[#2A3441] text-white"}`}>
              <Users className="h-5 w-5" />
            </button>
            <div className="w-px h-8 bg-[#2A3441] mx-1 hidden sm:block" />

            {/* Minimize button */}
            <button data-testid="minimize-btn-bar" onClick={minimizeMeeting}
              className="w-11 h-11 rounded-full bg-[#1E293B] hover:bg-[#2A3441] text-[#94A3B8] hover:text-white flex items-center justify-center transition-all"
              title="Reduire — la conference continue en arriere-plan">
              <Minimize2 className="h-5 w-5" />
            </button>

            {/* Quit button with confirmation */}
            <button data-testid="leave-btn" onClick={() => setShowQuitConfirm(true)}
              className="w-11 h-11 sm:w-auto sm:px-4 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center gap-2 transition-all">
              <PhoneOff className="h-5 w-5" />
              <span className="hidden sm:inline text-sm font-semibold">Quitter</span>
            </button>
          </div>
        </div>

        {/* Side panel */}
        {sidePanel && (
          <aside className="w-80 bg-[#0B0F19] border-l border-[#1E293B] flex flex-col shrink-0 max-sm:fixed max-sm:inset-y-0 max-sm:right-0 max-sm:z-50 max-sm:w-full max-sm:max-w-xs max-sm:shadow-2xl" data-testid="side-panel">
            <div className="h-12 border-b border-[#1E293B] flex items-center justify-between px-3 shrink-0">
              <div className="flex gap-1">
                {[
                  { key: "chat", label: "Chat" },
                  { key: "participants", label: "Participants" },
                ].map(t => (
                  <button key={t.key} onClick={() => setSidePanel(t.key)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${sidePanel === t.key ? "bg-white/10 text-white" : "text-[#64748b] hover:text-[#94A3B8]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <button onClick={() => { setSidePanel(null); setMobileSideOpen(false); }} className="text-[#64748b] hover:text-white p-1 sm:hidden">
                <X className="h-4 w-4" />
              </button>
            </div>

            {sidePanel === "chat" && (
              <div className="flex-1 min-h-0" data-testid="chat-panel">
                <EventChatWidget eventId={roomId} token={token} compact={true} />
              </div>
            )}

            {sidePanel === "participants" && (
              <div className="flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="participants-panel">
                {(isSimulation ? allSimParticipants : lk.lkParticipants.map(p => ({
                  id: p.identity, name: p.name || p.identity, role: "participant",
                  avatar: (p.name || "A").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
                  color: "#0099CC", micOn: p.isMicrophoneEnabled, camOn: p.isCameraEnabled,
                }))).map(p => {
                  const RIcon = ROLE_ICONS[p.role] || UserIcon;
                  return (
                    <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#151B2B] group transition-colors">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: p.color + "20", color: p.color }}>
                        {p.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-white text-sm font-medium truncate">{p.name}</span>
                          {p.id === "local" && <span className="text-[10px] text-[#0099CC]">(Vous)</span>}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-[#64748b]">
                          <RIcon className="h-2.5 w-2.5" />{ROLE_LABELS[p.role]}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {p.micOn ? <Mic className="h-3.5 w-3.5 text-[#10B981]" /> : <MicOff className="h-3.5 w-3.5 text-[#64748b]" />}
                        {p.camOn ? <Video className="h-3.5 w-3.5 text-[#3B82F6]" /> : <VideoOff className="h-3.5 w-3.5 text-[#64748b]" />}
                        {isSimulation && p.id !== "local" && (
                          <button onClick={() => toggleParticipantMic(p.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#2A3441] text-[#64748b] hover:text-white transition-all">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Quit confirmation modal */}
      {showQuitConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="quit-confirm-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Quitter la conference ?</h3>
                <p className="text-[#64748b] text-xs mt-0.5">Votre camera, micro et partage d'ecran seront coupes.</p>
              </div>
            </div>
            <p className="text-[#94A3B8] text-sm mb-5">
              Pour continuer a gerer votre evenement sans quitter la conference, utilisez le bouton <strong className="text-white">Reduire</strong> (<Minimize2 className="h-3 w-3 inline" />) a la place.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowQuitConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#1E293B] text-[#94A3B8] text-sm font-medium hover:bg-[#2A3441] transition-colors"
                data-testid="quit-cancel"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  lk.leaveRoom();
                  setShowQuitConfirm(false);
                  toast.success("Vous avez quitte la conference");
                  navigate(-1);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
                data-testid="quit-confirm"
              >
                Quitter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
